import { Cookies } from "./cookies";
import { JWTHelper } from "./jwt-helper";

interface ResponseAPI<T = any> {
  data: T;
  status: number;
  headers: Headers;
  url: string;
}

interface RequestInitOptionsProps extends RequestInit {
  params?: Record<string, any>;
  onProgress?: (loaded: number, total: number) => void;
}

type MiddlewareFunction<T extends ResponseAPI | RequestInitAPI> = (
  response: T,
) => Promise<T>;

type InitApiOptions =
  | {
      apiName: string;
      baseUrl: string;
      refreshTokenFunction?: (
        refreshToken: string,
      ) => Promise<{ newAccessToken: string; newRefreshToken?: string }>;
      expirationPropertyName?: string;
      onRedirectToLogin: () => void;
      tokenType?: "Bearer" | "Basic";
      ignoreAuth: (request: RequestInitAPI) => boolean;
    }
  | {
      apiName?: undefined;
      baseUrl?: undefined;
      refreshTokenFunction?: undefined;
      expirationPropertyName?: string;
      onRedirectToLogin?: undefined;
      tokenType?: undefined;
      ignoreAuth?: undefined;
    };

export type RequestInitAPI = RequestInitOptionsProps & {
  endpoint: string;
  baseUrl?: string;
  fullUrl: string;
  _isRetry?: boolean;
  _method?: string;
};

// Mapa global para compartilhar refreshInProgress entre instâncias com o mesmo apiName
const refreshInProgressMap = new Map<string, Promise<string> | null>();

export class API {
  private options: InitApiOptions;
  private responseMiddlewares: MiddlewareFunction<ResponseAPI>[] = [];
  private requestMiddlewares: MiddlewareFunction<RequestInitAPI>[] = [];
  private accessTokenKey: string | undefined;
  private refreshTokenKey: string | undefined;

  constructor(options: InitApiOptions = {}) {
    if (options.apiName) {
      options.apiName = options.apiName
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-zA-Z0-9]/g, "")
        .toLowerCase();
      this.accessTokenKey = `${options.apiName}_access_token`;
      this.refreshTokenKey = `${options.apiName}_refresh_token`;
    }
    const {
      expirationPropertyName = "exp",
      tokenType = "Bearer",
      ...rest
    } = options;

    this.options = {
      expirationPropertyName,
      tokenType,
      ...rest
    } as InitApiOptions;

    if (options.apiName) this.setup();
  }

  public getBaseUrl() {
    return this.options.baseUrl || "";
  }

  private setup() {
    this.useRequestMiddleware(async (request) => {
      if (this.options.ignoreAuth?.(request)) return request;

      const refreshToken = this.getRefreshToken();
      if (!refreshToken) {
        this.options.onRedirectToLogin?.();
        this.deleteTokens();
      } else {
        const updatedToken = await this.refreshToken();
        // Set Headers
        const Authorization = `${this.options.tokenType} ${updatedToken}`;
        request.headers = { ...request.headers, Authorization };
      }
      return request;
    });
  }

  public async refreshToken(forceRefresh = false): Promise<string> {
    const accessToken = this.getAccessToken();

    // Se já existe um refresh em progresso para este apiName, retorna a Promise existente
    if (
      this.options.apiName &&
      refreshInProgressMap.get(this.options.apiName)
    ) {
      return refreshInProgressMap.get(this.options.apiName)!;
    }

    if (this.options.refreshTokenFunction) {
      const { isValid, remainingMs } = this.validateTokenExpiration(
        accessToken!,
      );

      // Força refresh se forceRefresh=true OU se token inválido/perto de expirar
      if (
        (forceRefresh || !isValid || remainingMs < 5000) &&
        this.refreshTokenKey
      ) {
        const refreshToken = this.getRefreshToken();
        if (refreshToken) {
          // Cria uma nova Promise de refresh e armazena no mapa global
          const refreshPromise = (async () => {
            try {
              const { newAccessToken, newRefreshToken } =
                await this.options.refreshTokenFunction!(refreshToken);
              this.setAccessToken(newAccessToken);
              if (newRefreshToken) {
                this.setRefreshToken(newRefreshToken);
              }
              return newAccessToken;
            } catch (e) {
              this.deleteTokens();
              this.options.onRedirectToLogin?.();
              throw e;
            } finally {
              // Limpa o refresh em progresso após completar
              if (this.options.apiName) {
                refreshInProgressMap.set(this.options.apiName, null);
              }
            }
          })();

          if (this.options.apiName) {
            refreshInProgressMap.set(this.options.apiName, refreshPromise);
          }

          return refreshPromise;
        }
      }
    }
    return this.getAccessToken()!;
  }

  // Request Methods
  public async get<ResponseType = any>(
    endpoint: string,
    options: RequestInitOptionsProps = {},
  ) {
    const parsedUrl = this.handleUrl(endpoint, options);
    const requestData = await this.handleRequest({ ...options, ...parsedUrl });
    return this.executeRequest<ResponseType>(
      "GET",
      parsedUrl.fullUrl,
      requestData,
      options.onProgress,
    );
  }

  async post<ResponseType = any>(
    endpoint: string,
    body?: any,
    options: RequestInitOptionsProps = {},
  ) {
    const parsedUrl = this.handleUrl(endpoint, options);
    const requestData = await this.handleRequest({
      ...options,
      ...parsedUrl,
      body
    });
    return this.executeRequest<ResponseType>(
      "POST",
      parsedUrl.fullUrl,
      requestData,
      options.onProgress,
    );
  }

  async put<ResponseType = any>(
    endpoint: string,
    body?: any,
    options: RequestInitOptionsProps = {},
  ) {
    const parsedUrl = this.handleUrl(endpoint, options);
    const requestData = await this.handleRequest({
      ...options,
      ...parsedUrl,
      body
    });
    return this.executeRequest<ResponseType>(
      "PUT",
      parsedUrl.fullUrl,
      requestData,
      options.onProgress,
    );
  }

  public async delete<ResponseType = any>(
    endpoint: string,
    options: RequestInitOptionsProps = {},
  ) {
    const parsedUrl = this.handleUrl(endpoint, options);
    const requestData = await this.handleRequest({ ...options, ...parsedUrl });
    return this.executeRequest<ResponseType>(
      "DELETE",
      parsedUrl.fullUrl,
      requestData,
      options.onProgress,
    );
  }

  public async patch<ResponseType = any>(
    endpoint: string,
    body?: any,
    options: RequestInitOptionsProps = {},
  ) {
    const parsedUrl = this.handleUrl(endpoint, options);
    const requestData = await this.handleRequest({
      ...options,
      ...parsedUrl,
      body
    });
    return this.executeRequest<ResponseType>(
      "PATCH",
      parsedUrl.fullUrl,
      requestData,
      options.onProgress,
    );
  }
  //
  // Handlers
  private async executeRequest<ResponseType = any>(
    method: string,
    url: string,
    requestData: RequestInitAPI,
    onProgress?: (loaded: number, total: number) => void,
  ): Promise<ResponseAPI<ResponseType>> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.open(method, url, true);

      // Set headers
      const headers = requestData.headers as Record<string, string>;
      if (headers) {
        Object.entries(headers).forEach(([key, value]) => {
          xhr.setRequestHeader(key, value);
        });
      }

      // Progress tracking for upload
      if (onProgress && xhr.upload) {
        xhr.upload.addEventListener("progress", (e) => {
          if (e.lengthComputable) {
            onProgress(e.loaded, e.total);
          }
        });
      }

      // Handle response
      xhr.onload = async () => {
        try {
          const response = this.createResponseFromXHR(xhr);
          const result = await this.handleResponse<ResponseType>(response, url);
          resolve(result);
        } catch (error) {
          // Intercepta erro 401 e tenta renovar token
          if (
            error instanceof APIError &&
            error.statusCode === 401 &&
            !requestData._isRetry &&
            this.options.refreshTokenFunction
          ) {
            try {
              // Usa a função refreshToken existente forçando a renovação
              const newAccessToken = await this.refreshToken(true);

              // Reexecuta a requisição com o novo token
              const updatedRequest = {
                ...requestData,
                _isRetry: true
              };

              // Atualiza o header de autorização
              const newHeaders = { ...headers };
              newHeaders.Authorization = `${this.options.tokenType} ${newAccessToken}`;
              updatedRequest.headers = newHeaders;

              const retryResult = await this.executeRequest<ResponseType>(
                method,
                url,
                updatedRequest,
                onProgress,
              );
              resolve(retryResult);
              return;
            } catch (refreshError) {
              // Se o refresh falhar, rejeita com o erro original
              reject(error);
              return;
            }
          }
          reject(error);
        }
      };

      // Handle errors
      xhr.onerror = () => {
        reject(new APIError("Network error", 0));
      };

      xhr.ontimeout = () => {
        reject(new APIError("Request timeout", 0));
      };

      // Send request
      xhr.send((requestData.body as XMLHttpRequestBodyInit) || null);
    });
  }

  private createResponseFromXHR(xhr: XMLHttpRequest): Response {
    const headers = new Headers();
    const headerStr = xhr.getAllResponseHeaders() || "";
    const headerLines = headerStr.split("\r\n");
    headerLines.forEach((line) => {
      const parts = line.split(": ");
      if (parts.length === 2) {
        headers.append(parts[0], parts[1]);
      }
    });

    // Create a Response-like object
    const responseInit: ResponseInit = {
      status: xhr.status,
      statusText: xhr.statusText,
      headers
    };

    // Safely pick a body value that the Fetch `Response` constructor accepts.
    // XHR `response` can be various types depending on `responseType`; prefer
    // string/responseText or known binary types, and fallback to null.
    let body: BodyInit | null = null;
    try {
      if (xhr.response === undefined || xhr.response === null) {
        body = null;
      } else if (typeof xhr.response === "string") {
        body = xhr.response;
      } else if (
        typeof XMLSerializer !== "undefined" &&
        xhr.response instanceof Document
      ) {
        body = new XMLSerializer().serializeToString(xhr.response);
      } else if (typeof Blob !== "undefined" && xhr.response instanceof Blob) {
        body = xhr.response;
      } else if (
        typeof ArrayBuffer !== "undefined" &&
        xhr.response instanceof ArrayBuffer
      ) {
        body = xhr.response;
      } else {
        // Fallback to responseText which is always a string
        body = xhr.responseText || null;
      }
    } catch {
      body = xhr.responseText || null;
    }

    try {
      return new Response(body as any, responseInit);
    } catch {
      return new Response(null, responseInit);
    }
  }

  private async handleResponse<ResponseType = any>(
    response: Response,
    fullUrl: string,
  ) {
    // Caso de erro HTTP
    if (!response.ok) {
      let errorData: any = null;
      try {
        const contentType = response.headers.get("Content-Type") || "";
        if (contentType.includes("application/json")) {
          errorData = await response.json();
        } else {
          const text = await response.text();
          errorData = text || null;
        }
      } catch {
        errorData = null;
      }
      throw new APIError(errorData, response.status);
    }

    // Processamento normal - detecta tipo baseado no Content-Type
    let data: any = null;
    const contentType = response.headers.get("Content-Type") || "";

    try {
      if (contentType.includes("application/json")) {
        // JSON
        const text = await response.text();
        data = text ? JSON.parse(text) : null;
      } else if (
        contentType.includes("image/") ||
        contentType.includes("application/pdf") ||
        contentType.includes("application/octet-stream") ||
        contentType.includes("video/") ||
        contentType.includes("audio/")
      ) {
        // Blob (imagens, PDFs, arquivos binários, vídeos, áudio)
        data = await response.blob();
      } else if (
        contentType.includes("text/") ||
        contentType.includes("application/xml") ||
        contentType.includes("application/xhtml")
      ) {
        // Texto plano, HTML, XML
        data = await response.text();
      } else if (contentType.includes("multipart/form-data")) {
        // FormData (raro em respostas, mas possível)
        data = await response.formData();
      } else {
        // Fallback: tenta JSON, se falhar retorna texto
        const text = await response.text();
        try {
          data = text ? JSON.parse(text) : null;
        } catch {
          data = text || null;
        }
      }
    } catch {
      // Se falhar ao processar, tenta texto como fallback final
      try {
        data = await response.text();
      } catch {
        data = null;
      }
    }

    let responseResult: ResponseAPI<ResponseType> = {
      data,
      status: response.status,
      headers: response.headers,
      url: fullUrl
    };

    for (const middleware of this.responseMiddlewares) {
      responseResult = await middleware(responseResult);
    }

    return responseResult as ResponseAPI<ResponseType>;
  }

  private async handleRequest(request: RequestInitAPI) {
    // 2) Normalize headers to a mutable plain object
    let headers: Record<string, string> = {};
    if (request.headers) {
      if (request.headers instanceof Headers) {
        request.headers.forEach((value, key) => {
          headers[key] = value;
        });
      } else if (Array.isArray(request.headers)) {
        for (const [k, v] of request.headers) headers[k] = v as string;
      } else {
        headers = { ...(request.headers as Record<string, string>) };
      }
    }

    const hasHeader = (name: string) =>
      Object.keys(headers).some((h) => h.toLowerCase() === name.toLowerCase());

    const setHeaderIfMissing = (name: string, value: string) => {
      if (!hasHeader(name)) headers[name] = value;
    };

    // 3) Detect body type and set Content-Type only when necessary
    let bodyToSend: any = request.body;

    if (bodyToSend == null) {
      // no body: nothing to set
    } else if (
      typeof FormData !== "undefined" &&
      bodyToSend instanceof FormData
    ) {
      // Do NOT set Content-Type for FormData; browser will set boundary automatically
    } else if (
      typeof URLSearchParams !== "undefined" &&
      bodyToSend instanceof URLSearchParams
    ) {
      setHeaderIfMissing(
        "Content-Type",
        "application/x-www-form-urlencoded;charset=UTF-8",
      );
      // URLSearchParams can be sent directly
    } else if (
      (typeof File !== "undefined" && bodyToSend instanceof File) ||
      (typeof Blob !== "undefined" && bodyToSend instanceof Blob)
    ) {
      // Use the blob/file mime type if provided
      const type = (bodyToSend as Blob).type;
      if (type) setHeaderIfMissing("Content-Type", type);
    } else if (typeof bodyToSend === "string") {
      setHeaderIfMissing("Content-Type", "text/plain;charset=UTF-8");
    } else if (
      typeof bodyToSend === "number" ||
      typeof bodyToSend === "boolean"
    ) {
      setHeaderIfMissing("Content-Type", "text/plain;charset=UTF-8");
      bodyToSend = String(bodyToSend);
    } else if (typeof bodyToSend === "object") {
      // Object/Array -> JSON
      setHeaderIfMissing("Content-Type", "application/json;charset=UTF-8");
      bodyToSend = JSON.stringify(bodyToSend);
    }

    request = {
      ...request,
      headers: { ...headers, ...request.headers },
      body: bodyToSend
    };

    // 1) Execute request middlewares
    for (const middleware of this.requestMiddlewares) {
      request = await middleware(request);
    }

    return request as RequestInitAPI;
  }

  private handleUrl(endpoint: string, options: RequestInitOptionsProps) {
    if (!endpoint.startsWith("http")) {
      endpoint = endpoint.startsWith("/") ? endpoint : `/${endpoint}`;
      const query = options.params ? this.toQueryParams(options.params) : "";
      endpoint += query;
    }

    let baseUrl = "";
    if (this.options.baseUrl) {
      baseUrl = this.options.baseUrl.endsWith("/")
        ? this.options.baseUrl.slice(0, -1)
        : this.options.baseUrl;
    }

    return {
      endpoint,
      baseUrl: baseUrl || undefined,
      fullUrl: baseUrl + endpoint
    };
  }

  private toQueryParams(params: Record<string, any>): string {
    const query = Object.entries(params)
      .filter(([, value]) => value !== undefined && value !== null)
      .map(
        ([key, value]) =>
          `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`,
      )
      .join("&");

    return query ? `?${query}` : "";
  }

  //
  // Add Middleware
  public useResponseMiddleware(middleware: MiddlewareFunction<ResponseAPI>) {
    this.responseMiddlewares.push(middleware);
  }

  public useRequestMiddleware(middleware: MiddlewareFunction<RequestInitAPI>) {
    this.requestMiddlewares.push(middleware);
  }

  //
  // Token Methods
  private isValidJWTFormat(token: string): boolean {
    if (!token || typeof token !== "string") return false;
    const parts = token.split(".");
    return parts.length === 3;
  }

  public setTokens({
    accessToken,
    refreshToken
  }: {
    accessToken: string;
    refreshToken?: string;
  }) {
    this.setAccessToken(accessToken);
    if (refreshToken) {
      this.setRefreshToken(refreshToken);
    }
  }

  private setAccessToken(token: string) {
    if (!this.accessTokenKey)
      throw new Error("ApiName is required to set token");

    if (!this.isValidJWTFormat(token)) {
      console.error("Token inválido: formato JWT incorreto");
      throw new Error("Token deve ser um JWT válido com formato xxx.yyy.zzz");
    }

    try {
      const tokenPayload = JWTHelper.decodePayload(token);
      const expires = tokenPayload[this.options.expirationPropertyName!];
      Cookies.set(this.accessTokenKey, token, {
        expires: new Date(expires * 1000)
      });
    } catch (error) {
      console.error("Erro ao decodificar token:", error);
      throw new Error(
        "Falha ao processar token: token JWT inválido ou malformado",
      );
    }
  }

  public getAccessToken() {
    if (!this.accessTokenKey)
      throw new Error("ApiName is required to get token");
    return Cookies.get(this.accessTokenKey);
  }

  public async getValidAccessToken() {
    const accessToken = this.getAccessToken();
    const { isValid, remainingMs } = this.validateTokenExpiration(accessToken!);

    if (!isValid || remainingMs < 5000) {
      return await this.refreshToken();
    }

    return accessToken!;
  }

  private setRefreshToken(token: string) {
    if (!this.refreshTokenKey)
      throw new Error("ApiName is required to set token");

    if (!this.isValidJWTFormat(token)) {
      console.error("Refresh token inválido: formato JWT incorreto");
      throw new Error(
        "Refresh token deve ser um JWT válido com formato xxx.yyy.zzz",
      );
    }

    try {
      const tokenPayload = JWTHelper.decodePayload(token);
      const expires = tokenPayload[this.options.expirationPropertyName!];
      Cookies.set(this.refreshTokenKey, token, {
        expires: new Date(expires * 1000)
      });
    } catch (error) {
      console.error("Erro ao decodificar refresh token:", error);
      throw new Error(
        "Falha ao processar refresh token: token JWT inválido ou malformado",
      );
    }
  }

  public getRefreshToken() {
    if (!this.refreshTokenKey)
      throw new Error("ApiName is required to get token");
    return Cookies.get(this.refreshTokenKey);
  }

  private validateTokenExpiration(token: string) {
    if (!token) return { isValid: false, remainingMs: 0 };

    try {
      if (!this.isValidJWTFormat(token)) {
        console.warn("Token com formato inválido na validação de expiração");
        return { isValid: false, remainingMs: 0 };
      }

      const tokenPayload = JWTHelper.decodePayload(token);
      const expires = tokenPayload[this.options.expirationPropertyName!];

      if (!expires) {
        console.warn("Token sem campo de expiração");
        return { isValid: false, remainingMs: 0 };
      }

      const expiresMs = expires * 1000;
      const now = Date.now();

      return {
        isValid: expiresMs > now,
        expiresAt: new Date(expiresMs),
        remainingMs: expiresMs - now
      };
    } catch (error) {
      console.error("Erro ao validar expiração do token:", error);
      return { isValid: false, remainingMs: 0 };
    }
  }

  public hasSession() {
    if (!this.accessTokenKey || !this.refreshTokenKey) return false;
    if (this.options.refreshTokenFunction) {
      return (
        !!this.accessTokenKey &&
        !!this.refreshTokenKey &&
        !!this.getRefreshToken()
      );
    }
    const accessToken = this.getAccessToken();

    if (accessToken) {
      const { isValid } = this.validateTokenExpiration(accessToken);
      if (!isValid) {
        this.deleteTokens();
      }
    }

    return !!accessToken;
  }

  public deleteTokens() {
    if (this.accessTokenKey) Cookies.delete(this.accessTokenKey);
    if (this.refreshTokenKey) Cookies.delete(this.refreshTokenKey);
  }

  public getTokenPayload<T = any>() {
    const token = this.getAccessToken();
    if (token) return JWTHelper.decodePayload(token) as T;
    else throw new Error("Token não existente");
  }
}

export class APIError<T = any> extends Error {
  public statusCode: number;
  public data: T;

  constructor(data: T, statusCode = 400) {
    const message =
      typeof data === "string" ? data : `API Error: ${statusCode}`;
    super(message);
    this.name = "APIError";
    this.statusCode = statusCode;
    this.data = data;

    // Corrige o prototype para manter o instanceof funcionando
    Object.setPrototypeOf(this, APIError.prototype);
  }
}
